<template>
  <VLayout>
    <VFlex xs8>
      <VCard style="height: calc(100vh - 100px)">
        <VLayout justify-space-around row>
          <VFlex class="ml-5">
            <VCardTitle style="font-variant: small-caps">
              <h1>Države</h1>
              <v-spacer />
            </VCardTitle>
            <VContainer class="pa-0" fill-height fluid>
              <VLayout fill-height>
                <VFlex xs12>
                  <VDataTable
                    :headers="headers"
                    :items="countries"
                    :sort-by="['iso_code']"
                    :loading="countriesLoading"
                    rows-per-page-text="Vrstic na stran"
                  >
                    <template slot="items" slot-scope="props">
                      <td>
                        {{ props.item['iso_code'] }}
                      </td>
                      <td>
                        {{ props.item['name'] }}
                      </td>
                      <td>
                        {{ props.item['currency'] }}
                      </td>
                      <td>
                        <v-chip v-for="vat in props.item['vat_levels']">
                          <strong>{{ vat.percentage }}%</strong>&nbsp;
                          <span v-if="vat.note">({{ vat.note }})</span>
                        </v-chip>
                        <p v-if="!props.item['vat_levels'].length" class="ml-4">/</p>
                      </td>

                      <td class="text-xs-center">
                        <VTooltip top>
                          <VBtn
                            slot="activator"
                            flat
                            icon
                            color="red"
                            class="ma-auto"
                            @click.stop="deleteCountry(props.item)"
                          >
                            <VIcon>delete</VIcon>
                          </VBtn>
                          <span>Izbriši vnos</span>
                        </VTooltip>
                        <VTooltip top>
                          <VBtn
                            slot="activator"
                            flat
                            icon
                            color="green"
                            class="ma-auto"
                            @click.stop="startEditingCountry(props.item)"
                          >
                            <VIcon>edit</VIcon>
                          </VBtn>
                          <span>Uredi vnos</span>
                        </VTooltip>
                      </td>
                    </template>
                    <template slot="no-data"> Ni zadetkov :( </template>
                  </VDataTable>
                </VFlex>
              </VLayout>
            </VContainer>
          </VFlex>
        </VLayout>
      </VCard>
    </VFlex>
    <VFlex xs4>
      <VCard style="height: calc(100vh - 100px); overflow-y: scroll">
        <VLayout justify-space-around row>
          <VFlex xs10>
            <VCardTitle style="padding: 10px 0 0 0; font-variant: small-caps; margin-bottom: 10px">
              <h2>{{ currentlyEditing ? 'Uredi' : 'Dodaj' }} državo</h2>
            </VCardTitle>
            <VContainer fluid style="padding: 0 0 0 10px">
              <VLayout fill-height row wrap>
                <VFlex xs12>
                  <VTextField v-model="country.name" label="Naziv" type="string" />
                  <VTextField
                    v-model="country.iso_code"
                    label="ISO koda (2 znaka)"
                    type="string"
                    maxlength="2"
                  />
                  <VAutocomplete
                    v-model="country.currency"
                    :loading="!currencies"
                    :items="currencies"
                    :filter="filterAllCurrencies"
                    no-data-text="Ni zadetkov"
                    item-text="iso_code"
                    cache-items
                    clearable
                    label="Valuta"
                    flat
                  >
                    <template v-slot:item="data">
                      {{ data.item.iso_code }}
                    </template>
                  </VAutocomplete>
                  <div class="mt-2">
                    <VLabel>Davčne stopnje</VLabel>
                    <div>
                      <v-chip
                        v-for="(vat, ix) in country.vat_levels"
                        close
                        @input="country.vat_levels.splice(ix, 1)"
                      >
                        <strong>{{ vat.percentage }}%</strong>&nbsp;
                        <span v-if="vat.note">({{ vat.note }})</span>
                      </v-chip>
                      <p
                        v-if="!country.vat_levels.length"
                        class="ml-2 mb-2 mt-3"
                        style="font-size: 12px; color: dimgrey"
                      >
                        Za to državo še ni definiranih davčnih stopenj.
                      </p>
                    </div>
                    <div class="ml-2 mt-3">
                      <VLabel>Dodaj stopnjo</VLabel>
                      <VTextField
                        v-model="currentVatLevel.percentage"
                        label="Stopnja (v %)"
                        type="number"
                        max="100"
                        min="0"
                        @focusout="vatLevelToFixedDecimals"
                      ></VTextField>
                      <VTextField
                        v-model="currentVatLevel.note"
                        label="Opomba (opcijsko)"
                      ></VTextField>
                      <VBtn
                        color="primary"
                        :disabled="
                          !currentVatLevel.percentage ||
                          isNaN(currentVatLevel.percentage) ||
                          currentVatLevel.percentage > 100 ||
                          currentVatLevel.percentage < 0
                        "
                        class="v-btn--small right"
                        @click="addVatLevelToList(currentVatLevel)"
                      >
                        Dodaj stopnjo
                      </VBtn>
                    </div>
                  </div>
                </VFlex>
                <v-flex xs12 class="mt-2" style="display: flex; justify-content: flex-end">
                  <VBtn v-if="currentlyEditing" @click.stop="stopEditingCountry">
                    Prekliči urejanje
                  </VBtn>
                  <VBtn
                    color="primary"
                    :disabled="!country.name || country.iso_code.length !== 2 || !country.currency"
                    @click="createOrEditCountry"
                  >
                    Potrdi
                  </VBtn>
                </v-flex>
              </VLayout>
            </VContainer>
          </VFlex>
        </VLayout>
      </VCard>
    </VFlex>
  </VLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'Countries',
  data() {
    return {
      headers: [
        { text: 'ISO koda', value: 'iso_code', align: 'left', sortable: false },
        { text: 'Naziv', align: 'left', sortable: false },
        { text: 'Valuta', align: 'left', sortable: false },
        { text: 'Davčne stopnje', align: 'left', sortable: false },
        { text: 'Akcije', align: 'middle', sortable: false },
      ],
      countriesLoading: true,
      currentlyEditing: false,
      country: {
        iso_code: '',
        name: '',
        currency: undefined,
        vat_levels: [],
      },
      currentVatLevel: {
        note: '',
        percentage: undefined,
      },
    };
  },
  computed: {
    ...mapGetters({
      countries: 'countries',
      currencies: 'currencies',
    }),
  },
  created() {
    this.getCountries();
    this.getCurrenciesAction({});
  },
  watch: {
    country: {
      handler(newVal) {
        if (newVal.iso_code) {
          this.country.iso_code = newVal.iso_code.toUpperCase();
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions([
      'setSnackBarStateAction',
      'getCountriesAction',
      'getCurrenciesAction',
      'createCountryAction',
      'editCountryAction',
      'removeCountryAction',
    ]),
    async getCountries() {
      this.countriesLoading = true;
      await this.getCountriesAction({});
      this.countriesLoading = false;
    },
    vatLevelToFixedDecimals() {
      this.currentVatLevel.percentage = parseFloat(this.currentVatLevel.percentage).toFixed(2);
    },
    addVatLevelToList(vatLevel) {
      vatLevel.note = vatLevel.note.trim();
      this.country.vat_levels.push(JSON.parse(JSON.stringify(vatLevel)));
      this.currentVatLevel.percentage = undefined;
      this.currentVatLevel.note = '';
    },
    filterAllCurrencies(currency, queryText) {
      return currency.iso_code.toLowerCase().includes(queryText.toLowerCase());
    },
    startEditingCountry(country) {
      this.currentlyEditing = true;
      this.country = JSON.parse(JSON.stringify(country));
    },
    stopEditingCountry() {
      this.currentlyEditing = false;
      this.country = {
        iso_code: '',
        name: '',
        currency: undefined,
        vat_levels: [],
      };
    },
    showSnackbar(successful, msg) {
      const snackbarColor = successful ? 'green' : 'red';
      this.setSnackBarStateAction({
        snackBar: true,
        color: snackbarColor,
        text: msg,
      });
    },
    // ================================ CRUD operations ================================
    async createOrEditCountry() {
      this.countriesLoading = true;
      try {
        if (this.currentlyEditing) {
          await this.editCountryAction(this.country);
        } else {
          await this.createCountryAction(this.country);
        }
        this.showSnackbar(
          true,
          `${this.currentlyEditing ? 'Urejanje' : 'Dodajanje'} države je bilo uspešno.`
        );
        this.currentlyEditing = false;
        this.country = {
          iso_code: '',
          name: '',
          currency: undefined,
          vat_levels: [],
        };
      } catch (err) {
        this.showSnackbar(
          false,
          `Napaka pri ${
            this.currentlyEditing ? 'urejanju' : 'dodajanju'
          } države\n. Poskusite znova ali pokličite administratorja.`
        );
      } finally {
        this.countriesLoading = false;
      }
    },
    async deleteCountry(country) {
      this.countriesLoading = true;
      try {
        await this.removeCountryAction(country);
        this.showSnackbar(true, `Država ${country.name} je bila uspešno izbrisana.`);
      } catch (err) {
        this.showSnackbar(
          false,
          'Napaka pri brisanju države. Poskusite znova ali pokličite administratorja.'
        );
      } finally {
        this.countriesLoading = false;
      }
    },
    // ================================/ CRUD operations ================================
  },
};
</script>

<style scoped>
::v-deep .v-table__overflow {
  overflow: auto;
  max-height: 70vh;
}
</style>
